import type { ReactiveController, ReactiveControllerHost } from 'lit';

/**
 * AutoScrollController — automatically scrolls a container to the bottom
 * when new content is added, unless the user has scrolled up.
 *
 * Used in: loquix-message-list
 */
export class AutoScrollController implements ReactiveController {
  private host: ReactiveControllerHost & HTMLElement;
  private _scrollContainer: HTMLElement | null = null;
  private _isUserScrolled = false;
  private _observer: MutationObserver | null = null;
  private _threshold = 50; // px from bottom to consider "at bottom"

  constructor(host: ReactiveControllerHost & HTMLElement) {
    this.host = host;
    host.addController(this);
  }

  hostConnected(): void {
    // Will be set up after first render via attach()
  }

  hostDisconnected(): void {
    this.detach();
  }

  /**
   * Attach to a scrollable container element.
   * Call this in firstUpdated() or after shadow DOM is ready.
   */
  attach(container: HTMLElement): void {
    this.detach();
    this._scrollContainer = container;
    container.addEventListener('scroll', this._onScroll, { passive: true });

    // Observe child additions to auto-scroll
    this._observer = new MutationObserver(() => {
      if (!this._isUserScrolled) {
        this.scrollToBottom();
      }
    });
    this._observer.observe(container, { childList: true, subtree: true });
  }

  detach(): void {
    if (this._scrollContainer) {
      this._scrollContainer.removeEventListener('scroll', this._onScroll);
    }
    this._observer?.disconnect();
    this._observer = null;
    this._scrollContainer = null;
  }

  /** Check if the user is near the bottom */
  get isAtBottom(): boolean {
    if (!this._scrollContainer) return true;
    const { scrollTop, scrollHeight, clientHeight } = this._scrollContainer;
    return scrollHeight - scrollTop - clientHeight <= this._threshold;
  }

  /** Whether the user has scrolled away from bottom */
  get isUserScrolled(): boolean {
    return this._isUserScrolled;
  }

  /** Programmatically scroll to bottom */
  scrollToBottom(behavior: ScrollBehavior = 'smooth'): void {
    if (!this._scrollContainer) return;
    this._scrollContainer.scrollTo({
      top: this._scrollContainer.scrollHeight,
      behavior,
    });
    this._isUserScrolled = false;
  }

  private _onScroll = (): void => {
    this._isUserScrolled = !this.isAtBottom;
  };
}
